import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  FileText,
  Trophy,
  ClipboardCheck,
  Smile,
  Frown,
  Meh,
  Users,
  TrendingUp,
} from "lucide-react";
import { ResponsivePie } from "@nivo/pie";
import { Card } from "../../styles/GlobalStyles";

// Configuração de tema para os gráficos
const chartTheme = {
  background: "#FFFFFF",
  textColor: "#1E293B",
  fontSize: 14,
  axis: {
    domain: {
      line: {
        stroke: "#E2E8F0",
        strokeWidth: 1,
      },
    },
    legend: {
      text: {
        fontSize: 16,
        fontWeight: 600,
        fill: "#475569",
      },
    },
    ticks: {
      line: {
        stroke: "#E2E8F0",
        strokeWidth: 1,
      },
      text: {
        fontSize: 13,
        fill: "#64748B",
        fontWeight: 500,
      },
    },
  },
  grid: {
    line: {
      stroke: "#F1F5F9",
      strokeWidth: 1,
    },
  },
  legends: {
    title: {
      text: {
        fontSize: 15,
        fontWeight: 600,
        fill: "#475569",
      },
    },
    text: {
      fontSize: 13,
      fontWeight: 500,
      fill: "#64748B",
    },
  },
  tooltip: {
    container: {
      background: "#FFFFFF",
      color: "#1E293B",
      fontSize: 14,
      borderRadius: "8px",
      boxShadow:
        "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
      border: "1px solid #E2E8F0",
    },
  },
};

// Paleta de cores equilibrada - moderna e com presença
const professionalColors = [
  "#4A90E2", // Azul moderno
  "#5B9A3E", // Verde equilibrado
  "#E85C7A", // Rosa coral
  "#9C6ADE", // Roxo suave
  "#F5A623", // Laranja dourado
  "#50E3C2", // Turquesa
  "#E94B6B", // Rosa vibrante
  "#6B9A4A", // Verde lima suave
  "#6C7BE8", // Azul lavanda
  "#D4A574", // Caramelo
];

const InicioContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0;
`;

const PageHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const PageTitle = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize["2xl"]};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const ChartCard = styled(Card)`
  padding: ${({ theme }) => theme.spacing.lg};
  height: auto;
  display: flex;
  flex-direction: column;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.md};
  }
`;

const ChartTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${({ theme }) => theme.colors.border.light};
  margin: ${({ theme }) => theme.spacing.md} 0;
`;

const ChartContainer = styled.div`
  flex: 1;
  min-height: 300px;
  position: relative;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    min-height: 250px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    min-height: 200px;
  }
`;

// Cards do topo
const TopCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const TopCard = styled(Card)<{ borderColor: string }>`
  border-left: 4px solid ${({ borderColor }) => borderColor};
  padding: ${({ theme }) => theme.spacing.lg};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.md};
  }
`;

const TopCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
`;

const TopCardTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TopCardContent = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
`;

const TopCardLink = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    color: ${({ theme }) => theme.colors.primaryDark};
  }
`;

// Grid principal
const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

// Indicadores de sentimento
const SentimentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-top: 1rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    gap: 0.5rem;
  }
`;

const SentimentItem = styled.div`
  text-align: center;
`;

const SentimentIcon = styled.div<{ color: string }>`
  margin-bottom: 0.5rem;
  color: ${({ color }) => color};
  display: flex;
  justify-content: center;

  svg {
    width: 2rem;
    height: 2rem;

    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
      width: 1.5rem;
      height: 1.5rem;
    }
  }
`;

const SentimentPercentage = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: 1rem;
  }
`;

const SentimentLabel = styled.div`
  font-size: 0.875rem;
  color: #6b7280;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: 0.75rem;
  }
`;

// Cards de estatísticas inferiores
const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 2fr;
  gap: 1.5rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);

    /* Origem dos pontos ocupa 2 colunas no tablet */
    > :nth-child(3) {
      grid-column: 1 / -1;
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const StatsCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  text-align: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 1rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 0.875rem;
  }
`;

const StatsTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    color: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: 0.875rem;
  }
`;

const StatsNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 0.5rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 2rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: 1.75rem;
  }
`;

const StatsSubtitle = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 1rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: 0.75rem;
  }
`;

const StatsGrowth = styled.div<{ positive: boolean }>`
  font-size: 0.875rem;
  color: ${({ positive }) => (positive ? "#10b981" : "#ef4444")};
  font-weight: 500;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: 0.75rem;
  }
`;



// Hook para responsividade simples
const useResponsive = () => {
  const [screenSize, setScreenSize] = useState({
    isMobile: window.innerWidth <= 768,
    isTablet: window.innerWidth > 768 && window.innerWidth <= 1024,
    isDesktop: window.innerWidth > 1024,
  });

  useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        isMobile: window.innerWidth <= 768,
        isTablet: window.innerWidth > 768 && window.innerWidth <= 1024,
        isDesktop: window.innerWidth > 1024,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return screenSize;
};

// Configuração responsiva para legendas
const getResponsiveLegendConfig = (screenSize: any) => {
  const { isMobile } = screenSize;

  if (isMobile) {
    return {
      anchor: "bottom" as const,
      direction: "column" as const,
      justify: false,
      translateX: 0,
      translateY: 50,
      itemsSpacing: 8,
      itemWidth: 200,
      itemHeight: 18,
      itemTextColor: "#64748B",
      itemDirection: "left-to-right" as const,
      itemOpacity: 1,
      symbolSize: 12,
      symbolShape: "circle" as const,
    };
  }

  return {
    anchor: "bottom" as const,
    direction: "column" as const,
    justify: false,
    translateX: 0,
    translateY: 60,
    itemsSpacing: 8,
    itemWidth: 180,
    itemHeight: 20,
    itemTextColor: "#64748B",
    itemDirection: "left-to-right" as const,
    itemOpacity: 1,
    symbolSize: 12,
    symbolShape: "circle" as const,
  };
};

export const Inicio: React.FC = () => {
  const screenSize = useResponsive();
  // Dados do turnover
  const turnoverData = [
    {
      id: "demissao_solicitada",
      label: "Demissão solicitada",
      value: 53,
      color: professionalColors[0], // Azul moderno
    },
    {
      id: "demissao_empresa",
      label: "Demissão pela empresa",
      value: 47,
      color: professionalColors[1], // Verde equilibrado
    },
  ];

  // Dados da origem dos pontos
  const origemPontosData = [
    {
      id: "check_in",
      label: "Check-in",
      value: 30,
      color: "#10b981",
    },
    {
      id: "desafios",
      label: "Desafios",
      value: 25,
      color: "#8b5cf6",
    },
    {
      id: "jornadas",
      label: "Jornadas",
      value: 15,
      color: "#059669",
    },
    {
      id: "acesso_diario",
      label: "Acesso diário",
      value: 12,
      color: "#6366f1",
    },
    {
      id: "conecte",
      label: "Conecte",
      value: 10,
      color: "#f59e0b",
    },
    {
      id: "outros",
      label: "Outros",
      value: 8,
      color: "#374151",
    },
  ];

  // Dados do velocímetro para humor geral
  const humorData = [
    { id: "muito-triste", value: 20, color: "#ef4444" },
    { id: "triste", value: 20, color: "#f59e0b" },
    { id: "neutro", value: 20, color: "#10b981" },
    { id: "feliz", value: 20, color: "#3b82f6" },
    { id: "muito-feliz", value: 20, color: "#6366f1" },
  ];

  return (
    <InicioContainer>
      <PageHeader>
        <PageTitle>Olá, Joana!</PageTitle>
      </PageHeader>

      {/* Cards do topo */}
      <TopCardsGrid>
        <TopCard borderColor="#ef4444">
          <TopCardHeader>
            <FileText size={20} color="#ef4444" />
            <TopCardTitle>Entrevista de desligamento</TopCardTitle>
          </TopCardHeader>
          <TopCardContent>
            Há 3 documentos pendentes de assinatura
          </TopCardContent>
          <TopCardLink>
            Confira <span>→</span>
          </TopCardLink>
        </TopCard>

        <TopCard borderColor="#10b981">
          <TopCardHeader>
            <Trophy size={20} color="#10b981" />
            <TopCardTitle>Ranking mensal finalizado</TopCardTitle>
          </TopCardHeader>
          <TopCardContent>Clique para ver os resultados</TopCardContent>
          <TopCardLink>
            Confira <span>→</span>
          </TopCardLink>
        </TopCard>

        <TopCard borderColor="#3b82f6">
          <TopCardHeader>
            <ClipboardCheck size={20} color="#3b82f6" />
            <TopCardTitle>Avaliações de período de experiência</TopCardTitle>
          </TopCardHeader>
          <TopCardContent>
            Há 8 avaliações aguardando sua revisão
          </TopCardContent>
          <TopCardLink>
            Confira <span>→</span>
          </TopCardLink>
        </TopCard>
      </TopCardsGrid>

      {/* Grid principal */}
      <MainGrid>
        {/* Humor geral da população */}
        <ChartCard>
          <ChartTitle>
            <Smile size={20} />
            Humor geral da população
          </ChartTitle>
          <Divider />
          <ChartContainer>
            <ResponsivePie
              data={humorData}
              margin={{ top: 40, right: 40, bottom: 80, left: 40 }}
              startAngle={-90}
              endAngle={90}
              innerRadius={0.65}
              padAngle={0}
              cornerRadius={0}
              activeOuterRadiusOffset={0}
              colors={{ datum: "data.color" }}
              borderWidth={2}
              borderColor="#ffffff"
              enableArcLabels={false}
              enableArcLinkLabels={false}
              theme={{
                ...chartTheme,
                background: "transparent",
              }}
              animate={true}
              motionConfig="gentle"
              layers={[
                "arcs",
                ({ centerX, centerY, radius }) => (
                  <g>
                    {/* Score central */}
                    <text
                      x={centerX}
                      y={centerY + 20}
                      textAnchor="middle"
                      dominantBaseline="central"
                      style={{
                        fontSize: "2.5rem",
                        fontWeight: 800,
                        fill: "#1e293b",
                      }}
                    >
                      7,1
                    </text>

                    {/* Ponteiro - apontando para posição 7.1 de 10 considerando início em -180° */}
                    <g transform={`translate(${centerX}, ${centerY})`}>
                      <line
                        x1="0"
                        y1="0"
                        x2={
                          Math.cos(
                            ((-180 + (7.1 / 10) * 180) * Math.PI) / 180
                          ) *
                          (radius * 0.75)
                        }
                        y2={
                          Math.sin(
                            ((-180 + (7.1 / 10) * 180) * Math.PI) / 180
                          ) *
                          (radius * 0.75)
                        }
                        stroke="#1e293b"
                        strokeWidth="4"
                        strokeLinecap="round"
                      />
                      <circle cx="0" cy="0" r="6" fill="#1e293b" />
                    </g>
                  </g>
                ),
              ]}
            />
          </ChartContainer>

          <SentimentGrid>
            <SentimentItem>
              <SentimentIcon color="#ef4444">
                <Frown size={24} />
              </SentimentIcon>
              <SentimentPercentage>8%</SentimentPercentage>
              <SentimentLabel>Feliz</SentimentLabel>
            </SentimentItem>
            <SentimentItem>
              <SentimentIcon color="#3b82f6">
                <Smile size={24} />
              </SentimentIcon>
              <SentimentPercentage>72%</SentimentPercentage>
              <SentimentLabel>Muito feliz</SentimentLabel>
            </SentimentItem>
            <SentimentItem>
              <SentimentIcon color="#f59e0b">
                <Meh size={24} />
              </SentimentIcon>
              <SentimentPercentage>20%</SentimentPercentage>
              <SentimentLabel>Neutros</SentimentLabel>
            </SentimentItem>
          </SentimentGrid>
        </ChartCard>

        {/* Turnover */}
        <ChartCard>
          <ChartTitle>
            <TrendingUp size={20} />
            Turnover
          </ChartTitle>
          <Divider />
          <ChartContainer
            style={{
              height: screenSize.isMobile ? "400px" : "320px",
              width: "100%",
            }}
          >
            <ResponsivePie
              data={turnoverData}
              margin={
                screenSize.isMobile
                  ? { top: 10, right: 10, bottom: 70, left: 10 }
                  : { top: 20, right: 20, bottom: 60, left: 20 }
              }
              innerRadius={0.5}
              padAngle={2}
              cornerRadius={4}
              activeOuterRadiusOffset={8}
              borderWidth={2}
              borderColor="#e2e8f0"
              colors={{ datum: "data.color" }}
              enableArcLinkLabels={false}
              enableArcLabels={true}
              arcLabelsSkipAngle={10}
              arcLabelsTextColor="#FFFFFF"
              arcLabelsRadiusOffset={0.5}
              valueFormat={(value) => `${value}%`}
              isInteractive={true}
              animate={true}
              motionConfig="gentle"
              theme={{
                ...chartTheme,
                background: "transparent",
                labels: {
                  text: {
                    fontSize: screenSize.isMobile ? 12 : 16,
                    fontWeight: "bold",
                    fill: "#FFFFFF",
                  },
                },
                legends: {
                  ...chartTheme.legends,
                  text: {
                    fontSize: screenSize.isMobile ? 14 : 16,
                    fontWeight: 600,
                    fill: "#64748B",
                  },
                },
              }}
              legends={[
                {
                  ...getResponsiveLegendConfig(screenSize),
                  effects: [
                    {
                      on: "hover",
                      style: {
                        itemTextColor: "#1e293b",
                      },
                    },
                  ],
                },
              ]}
              layers={[
                "arcs",
                "arcLabels",
                ({ centerX, centerY }) => (
                  <text
                    x={centerX}
                    y={centerY}
                    textAnchor="middle"
                    dominantBaseline="central"
                    style={{
                      fontSize: screenSize.isMobile ? "1.5rem" : "2rem",
                      fontWeight: 800,
                      fill: "#1e293b",
                    }}
                  >
                    48%
                  </text>
                ),
                "legends",
              ]}
            />
          </ChartContainer>
        </ChartCard>
      </MainGrid>

      {/* Cards de estatísticas */}
      <StatsGrid>
        <StatsCard>
          <StatsTitle>
            <Users size={20} />
            Usuários ativos
          </StatsTitle>
          <StatsNumber>1090</StatsNumber>
          <StatsSubtitle>nos últimos 90 dias</StatsSubtitle>
          <StatsGrowth positive={true}>
            ↑ 12% em relação à última campanha
          </StatsGrowth>
        </StatsCard>

        <StatsCard>
          <StatsTitle>
            <Users size={20} />
            Participantes
          </StatsTitle>
          <StatsNumber>3.475</StatsNumber>
          <StatsSubtitle></StatsSubtitle>
          <StatsGrowth positive={true}>
            ↑ 18% em relação à última campanha
          </StatsGrowth>
        </StatsCard>

        <ChartCard>
          <ChartTitle>Origem dos pontos</ChartTitle>
          <Divider />
          <div
            style={{
              display: "flex",
              flexDirection: screenSize.isMobile ? "column" : "row",
              alignItems: screenSize.isMobile ? "center" : "flex-start",
              gap: screenSize.isMobile ? "1rem" : "1.5rem",
              height: screenSize.isMobile ? "auto" : "180px",
            }}
          >
            <div
              style={{
                flex: screenSize.isMobile ? "none" : "1",
                height: screenSize.isMobile ? "160px" : "100%",
                width: screenSize.isMobile ? "100%" : "auto",
              }}
            >
              <ResponsivePie
                data={origemPontosData}
                margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
                innerRadius={0.5}
                padAngle={2}
                cornerRadius={4}
                activeOuterRadiusOffset={8}
                borderWidth={1}
                borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
                colors={{ datum: "data.color" }}
                enableArcLinkLabels={false}
                enableArcLabels={false}
                isInteractive={true}
                animate={true}
                motionConfig="gentle"
                theme={{
                  ...chartTheme,
                  background: "transparent",
                }}
                legends={[]}
              />
            </div>
            <div
              style={{
                flex: screenSize.isMobile ? "none" : "1",
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
                justifyContent: "center",
              }}
            >
              {origemPontosData.map((item) => (
                <div
                  key={item.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <div
                    style={{
                      width: "12px",
                      height: "12px",
                      borderRadius: "50%",
                      backgroundColor: item.color,
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      fontSize: screenSize.isMobile ? "12px" : "14px",
                      fontWeight: 600,
                      color: "#1e293b",
                    }}
                  >
                    {item.value}%
                  </span>
                  <span
                    style={{
                      fontSize: screenSize.isMobile ? "12px" : "14px",
                      color: "#64748B",
                    }}
                  >
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </ChartCard>
      </StatsGrid>
    </InicioContainer>
  );
};
